/**
 * Pure logic for the Insiders claim form (COM-506). No I/O, no React — every
 * rule here is unit-tested without a DOM.
 */
import {
  buildSfraControllerPath,
  isSameOriginSfraPath,
} from "~/hec/utils/sfra-url";

import {
  claimRejectedSchema,
  claimSuccessSchema,
  csrfErrorSchema,
  errorStartSchema,
} from "./insiders-claim.schemas";
import type {
  ClaimOutcome,
  ClaimRequest,
  ClaimValues,
  LoyaltyData,
} from "./types";

export const FORM_DATA_ACTION = "Loyalty-FormData";
export const REDEEM_POINTS_ACTION = "Loyalty-RedeemPoints";

export const EMPTY_LOYALTY_DATA: LoyaltyData = {
  monthly: {},
  annualContribution: {},
  futurePlans: {},
};

/**
 * Pre-fill parity with `components/loyalty/form.isml`: the amount is the saved
 * string or empty, and "Yes, I plan to enroll" is selected unless the saved
 * answer is exactly `false` — a never-answered shopper sees Yes.
 */
export function getInitialClaimValues(
  data: LoyaltyData | null | undefined,
): ClaimValues {
  return {
    amount: data?.annualContribution.amount ?? "",
    answer: data?.futurePlans.answer !== false,
  };
}

/**
 * Form-encoded body for `Loyalty-RedeemPoints`.
 *
 * The controller reads `req.form` only — a JSON body leaves `type` undefined
 * and lands in the history-wiping default branch. `answer` must be the literal
 * strings `"true"` / `"false"` (`loyaltyHelper.js` compares `=== 'true'`), and
 * `amount` rides only with the annual form. Nothing goes in the query string:
 * SFRA drops a form key that also appears there.
 */
export function buildClaimBody({
  variant,
  csrf,
  values,
}: ClaimRequest): URLSearchParams {
  const body = new URLSearchParams();
  body.set("type", variant);
  body.set("csrf_token", csrf);
  if (variant === "annualContribution")
    body.set("amount", values.amount.trim());
  if (variant === "futurePlans")
    body.set("answer", values.answer ? "true" : "false");
  return body;
}

/**
 * The POST target. The server's `actionUrl` is preferred (it is the contract),
 * but only when it is a relative SFRA instance path — anything else is rebuilt
 * for the current site so the browser never posts cross-origin.
 */
export function resolveActionUrl(
  actionUrl: string | undefined,
  siteId: string,
): string {
  return actionUrl !== undefined && isSameOriginSfraPath(actionUrl)
    ? actionUrl
    : buildSfraControllerPath(siteId, REDEEM_POINTS_ACTION);
}

/**
 * Map one `Loyalty-RedeemPoints` reply to an outcome. Order matters: the CSRF
 * shape is checked first because it arrives on a 500 with its own keys, and a
 * success/rejection is always a 200 with `success`.
 */
export function classifyClaimResponse(
  status: number,
  json: unknown,
): ClaimOutcome {
  if (json === null || json === undefined || typeof json !== "object") {
    return status >= 500
      ? { kind: "serverError", message: null }
      : { kind: "malformed" };
  }
  if (csrfErrorSchema.safeParse(json).success) return { kind: "csrfExpired" };

  const success = claimSuccessSchema.safeParse(json);
  if (success.success) {
    return {
      kind: "success",
      message: success.data.message,
      redemptions: success.data.redemptions ?? null,
    };
  }
  const rejected = claimRejectedSchema.safeParse(json);
  if (rejected.success) {
    return {
      kind: "rejected",
      error: rejected.data.error,
      redemptions: rejected.data.redemptions ?? null,
    };
  }
  if (status >= 500) {
    const errorStart = errorStartSchema.safeParse(json);
    return {
      kind: "serverError",
      message: errorStart.success ? errorStart.data.message : null,
    };
  }
  return { kind: "malformed" };
}

/**
 * Annex's never-enrolled replies, lowercased. `Loyalty-RedeemPoints` passes
 * Annex's own `errorMsg` straight through as `error`, so these reach the card
 * verbatim ("No data found" was what COM-506 review saw on 2026-10-07).
 *
 * The opted-out case (`AC1009`) is deliberately NOT here: SFRA already maps it
 * to a `Resource.msgf` string that reads "Enroll now to claim" and carries its
 * own link, and that message must keep rendering as-is.
 */
const NOT_ENROLLED_ERRORS = ["no data found", "user not found"];

/**
 * Does a rejection mean "this shopper has no Perks membership"?
 *
 * A safety net, not the primary signal — enrollment is normally known up front
 * from the profile (`customerLoyalty`), and this only catches the case where
 * that blob was stale or missing. Markup and punctuation are stripped first
 * because the reply may or may not have been through `Resource.msgf`.
 */
export function isNotEnrolledError(error: string): boolean {
  const text = error
    .replace(/<[^>]*>/g, " ")
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .trim();
  return NOT_ENROLLED_ERRORS.some(phrase => text.includes(phrase));
}

/** The server echoes the whole blob back as `redemptions`; it replaces the local copy. */
export function mergeRedemptions(
  previous: LoyaltyData,
  redemptions: LoyaltyData | null | undefined,
): LoyaltyData {
  return redemptions ?? previous;
}
